import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar/component";
import { PageHeader } from "~/components/PageHeader";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { queryAssetFilterMeta, queryAssets } from "~/modules/assets/api/router";
import { Asset, AssetColumns } from "~/modules/assets/types";
import {
    FilterMetadata,
    FilterOperators,
    QueryResponse,
} from "~/modules/filters/types";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import {
    NEW_ASSET_MONITOR_FILTER,
    NEW_ASSET_MONITOR_QUERY,
} from "~/modules/new_asset_monitor/consts";
import { NewAssetMonitorListingPage } from "~/modules/new_asset_monitor/pages/listing";
import { SEARCH_FILTER_METADATA } from "~/modules/filters/consts";

// // // //

const SEO_TITLE = "New Asset Monitor";
const SEO_DESCRIPTION =
    "Monitor recently issued investable tokenized products across asset classes, with registration dates, issuance dates, issuer details, platform context, and primary market terms.";

// // // //

export async function getStaticProps() {
    // Make all requests in parallel + destructure responses
    // NOTE - only the first page of assets is fetched here, every other
    // page / sort is fetched client-side by NewAssetMonitorListingPage
    const [listQueryResponse, filterMetadata] = await Promise.all([
        queryAssets({
            query: NEW_ASSET_MONITOR_QUERY,
        }),
        queryAssetFilterMeta({
            filter: NEW_ASSET_MONITOR_FILTER,
        }),
    ]);

    // Trim down asset data to ONLY include the fields we need for the new asset monitor
    // This is necessary to keep the page size small enough for ISR (Incremental Static Regeneration) to work in Vercel
    const assets = listQueryResponse.results.map((a) => {
        return {
            id: a.id,
            name: a.name,
            _created_at: a._created_at,
            icon_url: a.icon_url,
            asset_class: a.asset_class,
            asset_class_id: a.asset_class_id,
            asset_class_name: a.asset_class_name,
            inception_date: a.inception_date,
            issuer: a.issuer,
            issuer_name: a.issuer_name,
            manager: a.manager,
            primary_market: a.primary_market,
            primary_markets: a.primary_markets,
            ticker: a.ticker,
            yield_to_maturity_percent: a.yield_to_maturity_percent,
            apy_30_day: a.apy_30_day,
            total_asset_value_dollar: a.total_asset_value_dollar,
            tokens: a.tokens.map((t) => {
                return {
                    id: t.id,
                    name: t.name,
                    address: t.address,
                    standards: t.standards,
                    platform: t.platform,
                    network: t.network,
                    asset_manager: t.asset_manager,
                };
            }),
        };
    });

    return {
        props: {
            listQueryResponse: {
                ...listQueryResponse,
                results: assets,
            },
            filterMetadata: filterMetadata
                .filter((fm) => {
                    return [
                        SEARCH_FILTER_METADATA.field,
                        AssetColumns.assetClassID,
                        AssetColumns.protocolIDs,
                        AssetColumns.networkIDs,
                    ].includes(fm.field);
                })
                .map((fm) => {
                    if (fm.defaultOperator !== FilterOperators.Includes) {
                        return fm;
                    }

                    // NOTE - InlineFilters uses the `equals` operator whenever it's
                    // allowed, which is an exact match on array columns (i.e.
                    // `network_ids equals [1]` excludes every asset that's also on
                    // another network). Dropping `equals` makes the platform +
                    // network filters fall back to their default `includes` operator.
                    return {
                        ...fm,
                        allowedOperators: fm.allowedOperators.filter(
                            (operator) => operator !== FilterOperators.Equals,
                        ),
                    };
                }),
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function NewAssetMonitorPage(props: {
    listQueryResponse: QueryResponse<Asset>;
    filterMetadata: FilterMetadata[];
}) {
    const { listQueryResponse, filterMetadata } = props;

    return (
        <PageTrack route={Routes.newAssetMonitor}>
            <NextSeo
                title={SEO_TITLE}
                canonical={addBaseUrl(Routes.newAssetMonitor)}
                description={SEO_DESCRIPTION}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.newAssetMonitor),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: "New Asset Monitor",
                        href: Routes.newAssetMonitor,
                    },
                ]}
            />

            <UserFetcher>
                {({ user }) => (
                    <Navbar route={Routes.newAssetMonitor} user={user} />
                )}
            </UserFetcher>

            <SidebarLayout pathname={Routes.newAssetMonitor}>
                <PageWrapper>
                    <PageHeader
                        title="New Asset Monitor"
                        subtitle="Track recently issued investable products across tokenized asset classes. Registration Date reflects when an asset was added to RWA.xyz, while Issuance Date reflects the first mint or subscription."
                    />

                    <NewAssetMonitorListingPage
                        initialState={listQueryResponse}
                        filterMetadata={filterMetadata}
                    />
                </PageWrapper>
            </SidebarLayout>
        </PageTrack>
    );
}
